import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/feature/VideoCard";
import FilterBar from "@/components/feature/FilterBar";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { categories } from "@/mocks/videos";
import { filterAndSort, getVideosByCategory, paginate, PAGE_SIZE, type VideoFilter } from "@/lib/videoUtils";
import { usePageMeta } from "@/hooks/usePageMeta";
import NotFound from "@/pages/NotFound";

export default function CategoryPage() {
  const { categoryId = "" } = useParams();
  const category = categories.find((c) => c.id === categoryId);

  const [filter, setFilter] = useState<VideoFilter>({ region: "全部", year: "全部", sort: "hot" });
  const [page, setPage] = useState(1);

  const list = useMemo(
    () => (category ? filterAndSort(getVideosByCategory(category.id), filter) : []),
    [category, filter]
  );

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const pageList = paginate(list, Math.min(page, totalPages), PAGE_SIZE);

  usePageMeta({
    title: category ? `多多视频 - ${category.name}` : "多多视频",
    description: category ? category.description : "多多视频影视资源分类浏览",
    keywords: category ? `多多视频,${category.name},${category.name}资源,在线影视` : "多多视频",
  });

  if (!category) {
    return <NotFound />;
  }

  const handleFilter = (next: VideoFilter) => {
    setFilter(next);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-background-950">
      <SiteNavbar />
      <main className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
        <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-secondary-400 md:text-sm">
          <Link to="/" className="hover:text-accent-400 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-base" />
          <span className="text-foreground-200">{category.name}</span>
        </nav>

        <div className="mt-5 flex flex-col gap-3 border-b border-background-800 pb-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-foreground-50 md:text-4xl">
              {category.name}频道
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-400">
              {category.description}
            </p>
          </div>
          <span className="flex items-center gap-1.5 whitespace-nowrap text-sm text-foreground-300">
            <i className="ri-play-list-2-line text-lg text-accent-400" />
            共 {list.length} 部作品
          </span>
        </div>

        <div className="mt-6">
          <FilterBar filter={filter} onChange={handleFilter} compact />
          <FilterBar filter={filter} onChange={handleFilter} />
        </div>

        <div className="mt-8">
          {pageList.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
                {pageList.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              <Pagination current={Math.min(page, totalPages)} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              title="没有找到符合筛选条件的作品"
              description="换个地区或年份试试，或者重置筛选条件，看看该分类下的全部精彩内容。"
              onReset={() => handleFilter({ region: "全部", year: "全部", sort: "hot" })}
              recommendations={getVideosByCategory(category.id)}
            />
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}